import React, { useContext, useEffect, useRef, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import {
    Avatar,
    Box,
    Button,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import { GridActionsCellItem, GridEditInputCell, useGridApiRef } from '@mui/x-data-grid-pro';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import PasswordIcon from '@mui/icons-material/Password';

import ProjectContext from '../contexts/ProjectContext';

import ShortDataGrid from '../components/datagrid/ShortDataGrid';


import { useAbortController } from '../contexts/AbortControllerContext';

import VerticalDivider from '../components/divider/VerticalDivider';

import ControlBar from '../components/structure/ControlBar';
import ControlButton from '../components/structure/ControlButton';
import ControlFeedback from '../components/structure/ControlFeedback';

import { itemIcons } from '../components/SideItems/SideItems';
import ControlDivider from '../components/structure/ControlDivider';

import SettingsComponent from '../components/settings/SettingsComponent';
import SettingsSidebar from '../components/settings/SettingsSidebar';
import UserContext from '../contexts/UserContext';
import {useFetch} from '../components/auth/useFetch';

function stringToColor(string) {
    let hash = 0;
    let i;
    for (i = 0; i < string.length; i += 1) {
      hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
}

function validateName(value){
    return new Promise((resolve) => {
        const nameRegex = /^[A-Za-z]+(?: [A-Za-z]+)*$/;
        resolve(nameRegex.test(value) ? null : 'Invalid Name')
    })
}

function NameEditInputCell(props) {
    const { error } = props
    return (
        <Tooltip
            open={error}
            title={error}
        >
            <GridEditInputCell {...props} />
        </Tooltip>
    )
}

function renderEditName(params) {
    return <NameEditInputCell {...params} />
}

export default function AccountManageView(props) {

    const viewname = 'Account Management'
    const { setIsLoading } = props
    const { isAuthenticated } = useAuth0();
    const projectContext = useContext(ProjectContext);
    const { konsolUser, setKonsolUser } = useContext(UserContext)

    const apiRef = useGridApiRef()
    const unsavedChangesRef = useRef({
            rowBeforeChange: {},
            rowAfterChange: {},
            settingsBeforeChange: {}
        })


    const [settings, setSettings] = useState({})
    const [settingsLabel, setSettingsLabel] = useState({})

    //Status Bar Variables
    const [isUnsavedChange, setIsUnsavedChange] = useState(false)
    const [status, setStatus] = useState('')
    const fetchWithAuth = useFetch()

    useEffect(() => {
        isAuthenticated && (() => {
            loadSettings()
        })()
    }, [isAuthenticated])

    const preProcessEditName = async (params) => {
        const errorMessage = await validateName(params.props.value.toString())
        return { ...params.props, error: errorMessage }
    }


    const userDatagridColumn = [
        {field: 'icon', headerName: '', renderCell: (params) => 
            <Avatar 
                sx={{   bgcolor: stringToColor(params.row.name ?? ''),
                    border: '2px solid #274333',
                    fontSize: '15px',
                    width: 35,
                    height: 35
                }}
            >
                {`${(params.row.name[0] ?? 'X').toUpperCase()}${(params.row.name[1] ?? 'X').toUpperCase()}`}
            </Avatar>
        },
        {
            field: 'given_name', headerName: 'First Name', editable: true, 
            preProcessEditCellProps: preProcessEditName, 
            renderEditCell: renderEditName,
        },
        {
            field: 'family_name', headerName: 'Last Name', editable: true,
            preProcessEditCellProps: preProcessEditName, 
            renderEditCell: renderEditName,
        },
        {field: 'role', headerName: 'Role'},
        { field: 'actions', headerName: 'Actions', width: 80, type:'actions',
            getActions: ({ id, row }) => [
                <GridActionsCellItem 
                    icon={
                        <PasswordIcon style={{ fill: 'rgb(100, 130, 200)' }}/>
                    }
                    label={"Reset Password"}
                    onClick={() => resetPassword()}
                    showInMenu
                />
            ]
        }

    ]


    const onUnsavedChange = () => {
        setIsUnsavedChange(true)
        setStatus('UNSAVED CHANGES')
    }

    const onSaveChange = () => {
        setIsUnsavedChange(false)
        setStatus('SAVED CHANGES')
        clearStatusAfterTimeout()
    }

    const onRevertChange = () => {
        setIsUnsavedChange(false)
        setStatus('')
    }

    const clearStatusAfterTimeout = () => {
        setTimeout(() => setStatus(''), 1000)
    }

    async function loadSettings(){
        setIsLoading(true)
        await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/user/settings/meta`, {
            headers:{'X-Project-ID': projectContext?.project_db}
        })
            .then((res) => res.json())
            .then((result) => setSettingsLabel({...result}))
            .then(() => setSettings(konsolUser?.user_metadata ?? {}))
            .finally(() => setIsLoading(false))

    }

    const resetRef = () => {
        unsavedChangesRef.current = { rowAfterChange: {}, rowBeforeChange: {}, settingsBeforeChange: {} }
    }

    const saveChanges = () => {
        if(Object.keys(unsavedChangesRef.current.settingsBeforeChange).length){
            saveUserMetadata()
        }
        if(Object.keys(unsavedChangesRef.current.rowAfterChange).length){
            updateUserData()
        }
        resetRef()
        onSaveChange()
    }

    const revertChanges = () => {
        if(Object.keys(unsavedChangesRef.current.settingsBeforeChange).length){
            setSettings(unsavedChangesRef.current.settingsBeforeChange)
        }
        if(Object.keys(unsavedChangesRef.current.rowBeforeChange).length){
            apiRef.current.updateRows([unsavedChangesRef.current.rowBeforeChange])
            setKonsolUser(prev => ({...prev, ...unsavedChangesRef.current.rowBeforeChange}) )
        }
        resetRef()
        onRevertChange()
    }


    const updateUserData = () => {
        setIsLoading(true)
        const userData = unsavedChangesRef.current.rowAfterChange
        fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/user/`, {
            method: "PATCH",
            body: JSON.stringify(userData),
            headers: { 
                "Content-type": "application/json",
                'X-Project-ID': projectContext?.project_db
            }
        })
        .finally(() => setIsLoading(false))
    }


    const saveUserMetadata = () => {
        setIsLoading(true)
        fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/user/settings/`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'X-Project-ID': projectContext?.project_db,
            },
            body: JSON.stringify(settings),
            redirect: 'follow'
        })
        .then(response => response.text())
        .then(result => {
            setKonsolUser((prev) => {
                return { ...prev, user_metadata: { ...prev.user_metadata, ...settings } }
            })
        })
        .finally(() => setIsLoading(false))
    }


    const resetPassword = () => {
        setIsLoading(true)
        fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/user/password/`, {
            headers: { 
                "Content-type": "application/json",
                'X-Project-ID': projectContext?.project_db
            }
        })
        .finally(() => setIsLoading(false))
    }

    const handleSettingsChange = (newData) => {
        setSettings(prevData => {
            if(!Object.keys(unsavedChangesRef.current.settingsBeforeChange).length){
                unsavedChangesRef.current.settingsBeforeChange = prevData
            }
            return { ...prevData, ...newData }
        });
        onUnsavedChange()
    };

    const handleDatagridCellEdit = (params, e) => {
        if(params.reason !== 'enterKeyDown'){
            e.defaultMuiPrevented = true
        }
    }

    const processRowUpdate = (newRow, oldRow) => {
        const updatedUserName = {
            given_name: newRow.given_name,
            family_name: newRow.family_name,
            name: `${newRow.given_name} ${newRow.family_name}`,
            nickname: `${newRow.given_name}.${newRow.family_name}`.replace(/\s/g,'').toLowerCase()
        }
        unsavedChangesRef.current.rowAfterChange = updatedUserName
        if(!Object.keys(unsavedChangesRef.current.rowBeforeChange).length){
            unsavedChangesRef.current.rowBeforeChange = oldRow
        }
        setKonsolUser(prev => ({...prev, ...updatedUserName}) )
        onUnsavedChange()
        return newRow
    }

    return (
        <React.Fragment>
            <Box 
                display="flex" 
                flexDirection="column" 
                height='100%'
                width='100%'
            >
                <Box
                    width='100%'   
                >
                    <ControlBar>
                        <Tabs
                            value={0}
                            indicatorColor="secondary"
                            variant="scrollable"
                            scrollButtons="auto">
                            <Tab
                                label={
                                    <Box display="flex" alignItems="center">
                                        {itemIcons[viewname]}
                                        <Box ml={1}>{viewname}</Box>
                                    </Box>
                                }
                                style={{ textTransform: 'none', fontWeight: 'bold', color: 'white' }} />
                        </Tabs>

                        <ControlDivider />
                        <Box
                            display='flex'
                            justifyContent='space-between'
                            width='150px'
                            border='1px solid rgb(125, 125, 125)'
                            borderRadius='10px'
                        >
                            <ControlButton
                                clicked={saveChanges}
                                title='Save'
                            >
                                <SaveIcon style={{ fill: 'rgb(255, 255, 255)'}} />
                            </ControlButton>
                            <ControlFeedback>
                                <Typography
                                    fontSize='10px'
                                    fontWeight={600}
                                    width='100%'
                                    color={isUnsavedChange ? 'rgb(255, 165, 0)' : 'rgb(176, 236, 175)'}
                                >
                                    {status}
                                </Typography>
                            </ControlFeedback>
                            <ControlButton
                                title='Revert Changes'
                                clicked={revertChanges}
                                disabled={
                                    Object.keys(unsavedChangesRef.current.rowAfterChange).length
                                    || Object.keys(unsavedChangesRef.current.settingsBeforeChange).length
                                    ? false : true
                                }
                            >
                                    <HistoryIcon style={{ fill: 'rgb(255, 255, 255)'}} />
                            </ControlButton>
                        </Box>
                    </ControlBar>
                </Box>
                <Box 
                    display="flex" 
                    flexDirection="row"
                    sx={{
                        flex: 1,
                        overflow: 'hidden',
                        backgroundColor: 'rgba(0,0,0,0.8)'
                    }}
                >
                    <Box
                        m={3}
                        minWidth='176px'
                        maxWidth='176px'
                        height="100%"
                        sx={{
                            overflowY: 'scroll',
                            // display: {
                            //     xs: 'none',
                            //     sm: 'block',
                            // }
                        }}
                    >
                        <SettingsSidebar 
                            data={settings}
                            label={settingsLabel}
                        />
                    </Box>
                    <VerticalDivider />
                    <Box
                        width='100%' 
                        height="100%"
                        m={3}
                        sx={{
                            overflowY: 'scroll',
                        }}
                    >
                        <Box
                            height='120px'
                        >
                            <ShortDataGrid
                                apiRef={apiRef}
                                rows={[{
                                    id: konsolUser?.sub ?? 0,
                                    role: (konsolUser?.project_roles ?? []).join(','),
                                    ...konsolUser
                                }]}
                                columns={userDatagridColumn}
                                hideFooter={true}
                                onCellEditStop={handleDatagridCellEdit}
                                processRowUpdate={processRowUpdate}
                                onProcessRowUpdateError={(e) => console.error(e)}
                            />
                        </Box>
                        <SettingsComponent
                            data={settings}
                            label={settingsLabel}
                            onChange={handleSettingsChange}
                        />
                    </Box>
                </Box>
            </Box>
        </React.Fragment>
    )
}